<template>
  <span class="signal-chip button" :class="[`kind-${item.kind}`, { 'removable': removable }]" @click="remove()">
    <img v-if="iconSrc" class="signal-chip-icon" :src="iconSrc" :alt="label" />
    <font-awesome-icon v-else-if="item.kind === 'yes'" :icon="['fas', 'check']" />
    <font-awesome-icon v-else-if="item.kind === 'no'" :icon="['fas', 'times']" />
    <font-awesome-icon v-else-if="item.kind === 'player'" :icon="['fas', 'user']" />
    <span class="signal-chip-label">{{ label }}</span>
    <font-awesome-icon v-if="removable" class="signal-chip-remove" :icon="['fas', 'times-circle']" />
  </span>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { getRoleImage } from "@/services/assets";
import { useGrimoireStore, useSignalsStore } from "@/stores";
import type { Role, SignalItem } from "@/types";

const { item, removable = false } = defineProps<{
  item: SignalItem;
  removable?: boolean;
}>();

const emit = defineEmits<{ remove: [] }>();

const grimoire = useGrimoireStore();
const signalsStore = useSignalsStore();
const label = computed(() => signalsStore.itemToText(item));

const remove = () => {
  if (removable) {
    emit("remove");
  }
};

const iconSrc = computed(() => {
  if (item.kind === "role") {
    const role = grimoire.rolesJSONbyId.get(String(item.value));
    return role ? getRoleImage(role) : "";
  }
  if (item.kind === "alignment") {
    // getRoleImage special-cases the ids "good" / "evil" -> the alignment png.
    return getRoleImage({ id: String(item.value) } as Role);
  }
  if (item.kind === "type") {
    return new URL(`../assets/icons/${item.value}.webp`, import.meta.url).href;
  }
  return "";
});
</script>

<style scoped lang="scss">
.signal-chip {
  display: inline-flex;
  align-items: center;
  line-height: 1.4;
  max-width: 100%;
  &:not(.removable) {
    cursor: default;
    color:white;
  }
}

.signal-chip-icon {
  width: 1.8em;
  height: 1.8em;
  object-fit: contain;
}

.signal-chip.kind-number .signal-chip-label {
  font-weight: bold;
  font-size: 1.2em;
}

.signal-chip-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.signal-chip-remove {
  margin-left: 0.3em;
  &:hover {
    color: red;
  }
}
</style>
